import { getEasternTimeFromDate, getLocalTimeFromEasternDate } from './daylight-savings-time';
import { FIRST_HOUR, FIRST_MINUTE, FIRST_SECOND, LAST_HOUR, LAST_MINUTE, LAST_SECOND,
	SECONDS_PER_FINANCE_DAY } from './finance-constants.js';
import { financeScale, financeScaleInvert } from './finance-time-scale';

export let intervalHelpers = {

	/**
	 * Returns a function which takes a date and returns
	 * the scaled date at the beginning of the financial day n days before that
	 */
	 startOfFinanceDayDaysAgo(n) {
		 return (from) => {
			//get the most recent valid date from the financial calendar
			let d = getEasternTimeFromDate(financeScaleInvert(from));
			d = new Date(d.getFullYear(), d.getMonth(), d.getDate(), FIRST_HOUR, FIRST_MINUTE, FIRST_SECOND);
			d = getLocalTimeFromEasternDate(d);
			return financeScale(d) - n * SECONDS_PER_FINANCE_DAY;
		};
	 },

	 /**
 	 * Returns a function which takes a date and returns
 	 * a date at the beginning of the financial day n days before that
 	 */
	endOfFinanceDayDaysAgo(n) {
		return (from) => {
			//get the most recent valid date from the financial calendar
			let d = getEasternTimeFromDate(financeScaleInvert(from));
			d = new Date(d.getFullYear(), d.getMonth(), d.getDate(), LAST_HOUR, LAST_MINUTE, LAST_SECOND);
			d = getLocalTimeFromEasternDate(d);
			return financeScale(d) - n * SECONDS_PER_FINANCE_DAY;
		};
	},


	/**
	 * Returns a function which takes a date and
	 * returns a scaled date n months before that.
	 */
	monthsAgo(n) {
		return (from) => {
			//get the most recent valid date from the financial calendar
			let d = getEasternTimeFromDate(financeScaleInvert(from));
			d = new Date(d.getFullYear(), d.getMonth()-n, d.getDate(), FIRST_HOUR, FIRST_MINUTE, FIRST_SECOND);
			d = getLocalTimeFromEasternDate(d);
			return financeScale(d);
		};
	},

	/**
	 * Returns a function which takes a date and
	 * returns a scaled date n years before that.
	 */
	yearsAgo(n) {
		return (from) => {
			//get the most recent valid date from the financial calendar
			let d = getEasternTimeFromDate(financeScaleInvert(from));
			d = new Date(d.getFullYear()-n, d.getMonth(), d.getDate(), FIRST_HOUR, FIRST_MINUTE, FIRST_SECOND);
			d = getLocalTimeFromEasternDate(d);
			return financeScale(d);
		};
	},

	/**
	 * Returns a function which takes a date and
	 * returns a scaled date "infinitely" before that.
	 */
	foreverAgo() {
		return () => -Infinity;
	}

};

/* eslint-disable no-magic-numbers */ // the are all pretty self explanatory
export let intervals = {
	'1d': intervalHelpers.startOfFinanceDayDaysAgo(0),
	'1w': intervalHelpers.startOfFinanceDayDaysAgo(5),
	'1m': intervalHelpers.monthsAgo(1),
	'3m': intervalHelpers.monthsAgo(3),
	'6m': intervalHelpers.monthsAgo(6),
	'1y': intervalHelpers.yearsAgo(1),
	'5y': intervalHelpers.yearsAgo(5),
	'10y': intervalHelpers.yearsAgo(10),
	max: intervalHelpers.foreverAgo()
};

/**
 * Get a financeScale scaled interval based on a string description such as "1m", which would return an interval
 * from financeScale of 1 month ago up to "to" param (defaults to financeScale of now).
 *
 * @param {String} label The label that corresponds to the key in the map of interval functions
 * @param {Integer} to [optional - defaults to now] financeScale'd time of when the interval should be based off of
 * @return {Array} Two element array.  First element is the adjusted start of the interval, second element is the to param
 */
export function labelToScaledTimeInterval(label, to) {
	if (!to) to = financeScale(Date.now());
	return [
		intervals[label.toLowerCase()](to),
		to
	];
}


/**
 * Get a regular date (not financeScaled) interval based on a string description such as "1m", which would return an interval
 * from 1 month ago up to "to" param (defaults to now).
 *
 * @param {String} label The label that corresponds to the key in the map of interval functions
 * @param {Integer} to [optional - defaults to now] financeScale'd time of when the interval should be based off of
 * @return {Array} Two element array.  First element is the adjusted start of the interval, second element is the to param
 */
export function labelToTimeInterval(label, to) {
	if (!to) to = Date.now();
	let [scaledBottom] = labelToScaledTimeInterval(label, financeScale(to));
	if (scaledBottom !== -Infinity) scaledBottom = financeScaleInvert(scaledBottom);
	return [scaledBottom, to];
}
